import type { HexColor, RgbaColor } from "../types.ts";

export const hexToRgba = (hex: HexColor): RgbaColor => {
  let hexWithoutHash = hex.replace("#", "");

  if (hexWithoutHash.length === 3) {
    hexWithoutHash = hexWithoutHash
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (hexWithoutHash.length === 6) hexWithoutHash += "FF";

  const r = parseInt(hexWithoutHash.slice(0, 2), 16);
  const g = parseInt(hexWithoutHash.slice(2, 4), 16);
  const b = parseInt(hexWithoutHash.slice(4, 6), 16);
  const a = parseInt(hexWithoutHash.slice(6, 8), 16) / 255;
  return { r, g, b, a };
};

export const hexToRgbaString = (hex: HexColor): string =>
  rgbaToString(hexToRgba(hex));

export const rgbaStringToRgba = (rgba: string): RgbaColor => {
  const [r, g, b, a = 1] = rgba
    .replaceAll(/[rgba()]/gu, "")
    .split(",")
    .map(Number);
  return { r, g, b, a };
};

export const rgbaToHex = (rgba: RgbaColor): HexColor => {
  const { r, g, b, a } = rgba;
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, "0");
  const alpha = a < 1 ? toHex(a * 255) : "";
  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alpha}`;
};

export const rgbaToString = (rgba: RgbaColor): string =>
  `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;

export const isHexColor = (color: string): color is HexColor =>
  /^#(?:[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/u.test(color);

export const isRgbaColor = (color: unknown): color is RgbaColor =>
  typeof color === "object" &&
  color !== null &&
  typeof (color as RgbaColor).r === "number" &&
  typeof (color as RgbaColor).g === "number" &&
  typeof (color as RgbaColor).b === "number" &&
  typeof (color as RgbaColor).a === "number" &&
  (color as RgbaColor).r >= 0 &&
  (color as RgbaColor).r <= 255 &&
  (color as RgbaColor).g >= 0 &&
  (color as RgbaColor).g <= 255 &&
  (color as RgbaColor).b >= 0 &&
  (color as RgbaColor).b <= 255 &&
  (color as RgbaColor).a >= 0 &&
  (color as RgbaColor).a <= 1;
